import { useEffect, useState } from 'react';
import { Err } from '../ui.js';
import {
  listTeam, addTeamMember, setTeamRole, removeTeamMember,
  changePassword, setSecret, ApiError,
} from '../api.js';
import type { TeamMember } from '../api.js';

const ROLES = [
  ['owner', 'Everything, including other owners'],
  ['admin', 'Everything except removing the last owner'],
  ['member', 'The CRM — accounts, contacts, sequences, sending'],
  ['viewer', 'Read only'],
] as const;

// WHO IS IN THIS WORKSPACE, AND YOUR OWN ACCOUNT.
//
// The role descriptions sit next to the picker rather than in a help doc,
// because the moment someone chooses a role is the only moment they care what
// it means. `viewer` is the one that earns its place: a fractional operator
// hands a client read access to their own pipeline, and doing that with a
// member seat would let the client send mail as the practice.

export function Team() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [yourRole, setYourRole] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ email: '', name: '', role: 'member', password: '' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await listTeam();
      setMembers(r.members); setCanManage(r.can_manage); setYourRole(r.your_role);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }
  useEffect(() => { void load(); }, []);

  async function add() {
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await addTeamMember({
        email: f.email.trim(), name: f.name.trim() || undefined,
        role: f.role, password: f.password || undefined,
      });
      setNote(r.note);
      setF({ email: '', name: '', role: 'member', password: '' });
      setAdding(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <h4>Team</h4>
      <p className="meta">
        {canManage
          ? 'Everyone who can open this workspace, and what they can do in it.'
          : `You are a ${yourRole ?? 'member'} here — you can see the team but not change it.`}
      </p>

      <Err>{error}</Err>
      {note && <p className="warnbox">{note}</p>}

      {members.map((m) => (
        <div className="row" key={m.id}>
          <span className="rname">
            {m.name ?? m.email}
            {m.is_you && <span className="meta"> · you</span>}
          </span>
          <span className="meta">{m.email}</span>
          {m.is_superadmin && <span className="flag flag-mute">superadmin</span>}
          {!m.can_sign_in && (
            <span className="flag flag-warn" title="No password set — cannot sign in">
              no password
            </span>
          )}
          {canManage && !m.is_you ? (
            <select value={m.role} className="rolepick"
              onChange={(e) => void setTeamRole(m.id, e.target.value).then(load)
                .catch((x) => setError(x instanceof ApiError ? x.message : String(x)))}>
              {ROLES.map(([v]) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : (
            <span className="flag flag-mute">{m.role}</span>
          )}
          {canManage && !m.is_you && (
            <button className="linkish danger"
              onClick={() => void removeTeamMember(m.id).then((r) => { setNote(r.note); return load(); })
                .catch((x) => setError(x instanceof ApiError ? x.message : String(x)))}>
              Remove
            </button>
          )}
        </div>
      ))}

      {canManage && (adding ? (
        <div className="offeringform">
          <div className="pair">
            <label className="field">
              <span className="field-label">Email</span>
              <input type="email" value={f.email} autoFocus
                onChange={(e) => setF({ ...f, email: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Name</span>
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </label>
          </div>

          <label className="field">
            <span className="field-label">Role</span>
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {ROLES.map(([v, why]) => <option key={v} value={v}>{v} — {why}</option>)}
            </select>
          </label>

          <label className="field">
            <span className="field-label">Temporary password</span>
            <input type="password" value={f.password} autoComplete="new-password"
              onChange={(e) => setF({ ...f, password: e.target.value })} />
            {/* Said plainly, because it is the surprising part. */}
            <span className="field-hint">
              There is no verified sending domain yet, so the app cannot email an
              invite — an invite nobody can act on is worse than none. Set a
              password, pass it on however you already talk to them, and they
              change it once inside. Not needed if they already have an account.
            </span>
          </label>

          <div className="actions">
            <button className="primary" disabled={busy || !f.email.trim()} onClick={() => void add()}>
              {busy ? 'Adding…' : 'Add to workspace'}
            </button>
            <button className="ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="ghost" onClick={() => setAdding(true)}>Add someone</button>
      ))}
    </section>
  );
}

/** Your own account: the one thing every user can change about themselves. */
export function YourAccount({ email, name }: { email: string; name: string | null }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mismatch = again.length > 0 && next !== again;

  async function save() {
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await changePassword(current, next);
      // The server killed every session including this one and issued a fresh
      // token. Store it or the next request 401s and dumps you at the door.
      setSecret(r.token);
      setNote(r.note);
      setCurrent(''); setNext(''); setAgain('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <h4>Your account</h4>
      <p className="meta">{name ? `${name} · ${email}` : email}</p>

      <Err>{error}</Err>
      {note && <p className="warnbox">{note}</p>}

      <label className="field">
        <span className="field-label">Current password</span>
        <input type="password" value={current} autoComplete="current-password"
          onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <div className="pair">
        <label className="field">
          <span className="field-label">New password</span>
          <input type="password" value={next} autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Again</span>
          <input type="password" value={again} autoComplete="new-password"
            onChange={(e) => setAgain(e.target.value)} />
          {mismatch && <span className="field-hint">Those two do not match.</span>}
        </label>
      </div>
      <p className="fieldhint">
        At least 10 characters. Changing it ends every other sign-in you have —
        which is the point of changing it.
      </p>

      <div className="actions">
        <button className="primary"
          disabled={busy || !current || next.length < 10 || mismatch}
          onClick={() => void save()}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </section>
  );
}
